import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import DealerLayout from '../components/DealerLayout';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { C } from '../theme';

// Where the money for a renewal came from
const SRC_CFG = {
  admin:          { label: 'Admin renewal',  icon: '🧑‍💼', bg: '#EDE9FE', color: '#5B21B6' },
  dealer_request: { label: 'Dealer request', icon: '🏪',   bg: '#F0FDFA', color: '#0F766E' },
  restore:        { label: 'Restored',       icon: '↩',    bg: '#DBEAFE', color: '#1D4ED8' },
};

export default function RenewalHistory() {
  const { user } = useAuth();
  const isDealer = user?.role === 'dealer';
  const LayoutComp = isDealer ? DealerLayout : Layout;

  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('own');   // own | dealer  (admin only)
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/renewal-history');
      setRows(r.data);
    } catch { setRows([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Admin splits own vs dealer-managed clients. A dealer only ever has their own.
  const ownRows    = useMemo(
    () => (isDealer ? rows : rows.filter(r => !r.is_dealer_client)),
    [rows, isDealer]
  );
  const dealerRows = useMemo(
    () => (isDealer ? [] : rows.filter(r => r.is_dealer_client)),
    [rows, isDealer]
  );

  const pool = isDealer ? ownRows : (tab === 'dealer' ? dealerRows : ownRows);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(r =>
      [r.ref, r.mapping_ref, r.client_name, r.client_ref, r.dealer_name,
       r.category_name, r.type_name, r.serial_number, r.item_id]
        .some(v => v?.toLowerCase?.().includes(q))
    );
  }, [pool, search]);

  const summary = useMemo(() => ({
    count:   filtered.length,
    amount:  filtered.reduce((s, r) => s + (r.amount || 0), 0),
    months:  filtered.reduce((s, r) => s + (r.months || 0), 0),
    clients: new Set(filtered.map(r => r.client_id)).size,
  }), [filtered]);

  const showDealerCol = !isDealer && tab === 'dealer';

  const cols = [
    'Ref', 'Client',
    ...(showDealerCol ? ['Dealer'] : []),
    'Category / Type', 'Serial No', 'Period Covered', 'Term', 'Amount', 'Renewed On', 'Source',
  ];

  return (
    <LayoutComp title="Renewal History">

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', gap: '14px', marginBottom: '22px' }}>
        <SCard icon="🔁" label="Renewals"        value={summary.count}                                          sub={`${summary.clients} client${summary.clients !== 1 ? 's' : ''}`} dot="#7C3AED" bg="#EDE9FE" color="#5B21B6" />
        <SCard icon="💰" label="Total Collected" value={`₹${summary.amount.toLocaleString('en-IN')}`}           sub={isDealer ? 'debited from your wallet' : 'across all renewals'} dot="#10B981" bg="#D1FAE5" color="#065F46" />
        <SCard icon="📅" label="Term Added"      value={`${summary.months} mo`}                                 sub="total subscription extended" dot="#F59E0B" bg="#FEF3C7" color="#92400E" />
        {!isDealer && (
          <SCard icon="🏪" label="Dealer Renewals" value={dealerRows.length} sub={`${ownRows.length} for our own clients`} dot="#0D9488" bg="#F0FDFA" color="#0F766E" />
        )}
      </div>

      <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>

        {/* Tabs + Search */}
        <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {!isDealer && (
            <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', borderRadius: '10px', padding: '4px' }}>
              {[
                { key: 'own',    label: 'Our Clients',    count: ownRows.length,    solid: '#5B21B6' },
                { key: 'dealer', label: 'Dealer Clients', count: dealerRows.length, solid: '#0F766E' },
              ].map(t => {
                const active = tab === t.key;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)} style={{
                    padding: '6px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: 700,
                    border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                    background: active ? t.solid : 'transparent',
                    color:      active ? '#fff'  : C.textSub,
                    boxShadow:  active ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                  }}>
                    {t.label}
                    {t.count > 0 && (
                      <span style={{
                        marginLeft: '6px', padding: '1px 7px', borderRadius: '20px', fontSize: '10px', fontWeight: 800,
                        background: active ? 'rgba(255,255,255,0.28)' : '#E5E7EB',
                        color:      active ? '#fff' : C.textSub,
                      }}>
                        {t.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <input placeholder="Search ref, client, serial, category…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '8px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
          {search && (
            <button onClick={() => setSearch('')} style={{ padding: '8px 14px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
              Clear
            </button>
          )}
          <button onClick={load} title="Refresh" style={{ padding: '8px 12px', background: C.primaryBg, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>↻</button>
          <span style={{ fontSize: '12px', color: C.textSub, whiteSpace: 'nowrap' }}>{filtered.length} records</span>
        </div>

        {/* Context note */}
        <div style={{ padding: '10px 22px', background: '#F5F3FF', borderBottom: '1px solid #DDD6FE', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '13px' }}>📜</span>
          <span style={{ fontSize: '12px', color: '#5B21B6', fontWeight: 500 }}>
            {isDealer
              ? <>Renewal requests you raised that admin has <strong>completed</strong>. Each one extended that device's subscription by a full period, charged to your wallet.</>
              : tab === 'dealer'
                ? <>Completed renewals for <strong>dealer-managed clients</strong> — raised as dealer requests you approved, and charged to the dealer's wallet.</>
                : <>Completed renewals for <strong>our own clients</strong> — recorded once the AMC renewal is <strong>paid</strong> in the Credit List.</>}
          </span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
            <thead>
              <tr style={{ background: '#F5F3FF' }}>
                {cols.map(h => (
                  <th key={h} style={{ padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: '#5B21B6', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #DDD6FE', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={cols.length} style={{ textAlign: 'center', padding: '60px', color: C.textSub, fontSize: '13px' }}>Loading renewal history…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={cols.length} style={{ textAlign: 'center', padding: '60px' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>📜</div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain }}>No renewals yet</div>
                  <div style={{ fontSize: '12px', color: C.textSub, marginTop: '5px', lineHeight: 1.6 }}>
                    {search ? 'No records match your search.'
                      : isDealer
                        ? 'Raise a renewal request from the Renewal List. Once admin marks it completed, it appears here.'
                        : tab === 'dealer'
                          ? 'Approve a dealer renewal request from the Renewal List and it will be recorded here.'
                          : 'Use "Renew Now" in the Renewal List, then collect the payment in Credit List — the renewal is recorded here once fully paid.'}
                  </div>
                  {!isDealer && !search && (tab === 'own' ? dealerRows.length > 0 : ownRows.length > 0) && (
                    <button onClick={() => setTab(tab === 'own' ? 'dealer' : 'own')}
                      style={{ marginTop: '14px', padding: '7px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, border: 'none', cursor: 'pointer', background: tab === 'own' ? '#0F766E' : '#5B21B6', color: '#fff' }}>
                      {tab === 'own'
                        ? `🏪 ${dealerRows.length} renewal${dealerRows.length !== 1 ? 's' : ''} for Dealer Clients`
                        : `🧑‍💼 ${ownRows.length} renewal${ownRows.length !== 1 ? 's' : ''} for Our Clients`}
                    </button>
                  )}
                </td></tr>
              ) : filtered.map((r, i) => {
                const src = SRC_CFG[r.source] || SRC_CFG.admin;
                return (
                  <tr key={r.id} style={{ background: i % 2 === 0 ? C.white : '#FDFCFF', borderBottom: '1px solid #EDE9FE' }}>
                    {/* Ref */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: '#5B21B6', background: '#EDE9FE', padding: '3px 8px', borderRadius: '6px', display: 'inline-block' }}>
                        {r.ref || '—'}
                      </span>
                      <div style={{ fontFamily: 'monospace', fontSize: '10px', color: C.textSub, marginTop: '3px' }}>{r.mapping_ref}</div>
                    </td>
                    {/* Client */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#7C3AED,#5B21B6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: '#fff' }}>
                          {(r.client_name || 'C')[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>{r.client_name}</div>
                          <div style={{ fontSize: '11px', color: C.textSub }}>{r.client_ref}</div>
                        </div>
                      </div>
                    </td>
                    {/* Dealer */}
                    {showDealerCol && (
                      <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#0F766E', background: '#F0FDFA', padding: '3px 10px', borderRadius: '8px', whiteSpace: 'nowrap' }}>
                          🏪 {r.dealer_name || '—'}
                        </span>
                      </td>
                    )}
                    {/* Category / Type */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: C.textMain }}>{r.category_name}</div>
                      {r.type_name && (
                        <span style={{ fontSize: '11px', background: C.primaryBg, color: C.primaryDeep, padding: '1px 8px', borderRadius: '10px', fontWeight: 600, display: 'inline-block', marginTop: '2px' }}>
                          {r.type_name}
                        </span>
                      )}
                    </td>
                    {/* Serial */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: C.textMain }}>{r.serial_number || r.item_id || '—'}</div>
                      {r.ref_number && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: C.textSub, marginTop: '1px' }}>Ref: {r.ref_number}</div>}
                    </td>
                    {/* Period covered */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#5B21B6' }}>
                        <span>{r.period_from || '—'}</span>
                        <span style={{ color: '#A78BFA' }}>→</span>
                        <span>{r.period_to || '—'}</span>
                      </div>
                    </td>
                    {/* Term */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        +{r.months} mo
                      </span>
                    </td>
                    {/* Amount */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: '15px', fontWeight: 900, color: '#5B21B6' }}>₹{(r.amount || 0).toLocaleString('en-IN')}</div>
                      <div style={{ fontSize: '10px', color: C.textSub }}>
                        {r.charged_to === 'dealer_wallet' ? '👛 dealer wallet' : '💳 client credit'}
                      </div>
                    </td>
                    {/* Renewed on */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle', fontSize: '12px', color: C.textSub, whiteSpace: 'nowrap' }}>
                      {r.renewed_date}
                    </td>
                    {/* Source */}
                    <td style={{ padding: '13px 14px', verticalAlign: 'middle' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: src.bg, color: src.color, padding: '4px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {src.icon} {src.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {filtered.length > 0 && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid #DDD6FE', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center', background: '#FDFCFF' }}>
            <span style={{ fontSize: '12px', color: '#7C3AED', fontWeight: 600 }}>SHOWING {filtered.length} OF {pool.length} RECORDS</span>
            <span style={{ color: '#DDD6FE' }}>|</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: C.textSub }}>Total collected:</span>
              <span style={{ fontSize: '14px', fontWeight: 800, color: '#5B21B6' }}>₹{summary.amount.toLocaleString('en-IN')}</span>
            </div>
            <span style={{ color: '#DDD6FE' }}>|</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: C.textSub }}>Term added:</span>
              <span style={{ fontSize: '14px', fontWeight: 800, color: '#92400E' }}>{summary.months} months</span>
            </div>
          </div>
        )}
      </div>
    </LayoutComp>
  );
}

function SCard({ icon, label, value, sub, dot, bg, color }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span style={{ fontSize: '22px' }}>{icon}</span>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot, marginTop: '4px' }} />
      </div>
      <div style={{ fontSize: '22px', fontWeight: 800, color: '#111', marginBottom: '4px' }}>{value}</div>
      <div style={{ fontSize: '12px', fontWeight: 700, color, background: bg, display: 'inline-block', padding: '2px 8px', borderRadius: '6px', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>{sub}</div>
    </div>
  );
}
